import { inject, Injectable } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { AvatarOrderDataResponse, AvatarOrderResponse } from '@api/client';
import { ConnectAudioService } from '@services/connect-audio.service';
import { CHAT_CALL_STATUS } from '@chat/interfaces/chat.interfaces';
import { AuthService } from '@services/auth.service';
import { LoadingService } from '@services/loading.service';
import { ToastService } from '@services/toast.service';
import { WebSocketController } from '@services/web-socket.controller';
import { NavigationStore } from '@services/navigation.store';
import { NavController } from '@ionic/angular';
import { filter, firstValueFrom, timeout } from 'rxjs';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { CallSessionService } from '@call/services/call-session.service';
import { CallOrderService } from '@call/services/call-order.service';
import { CallPermissionsService } from '@call/services/call-permissions.service';
import { CALL_ORDER_STATUS_NUMBERS, CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';

export interface IncomingCallOpenResult {
  readonly opened: boolean;
  readonly navigationCommands: (string | number)[] | null;
  readonly orderId: number | null;
  readonly shouldAcceptAudioInBackground: boolean;
  readonly audioOrderResponse: AvatarOrderDataResponse | null;
}

@Injectable({
  providedIn: 'root'
})
export class AudioCallService {
  private readonly authService: AuthService = inject(AuthService);
  private readonly callSessionService: CallSessionService = inject(CallSessionService);
  private readonly connectAudioService: ConnectAudioService = inject(ConnectAudioService);
  private readonly callOrderService: CallOrderService = inject(CallOrderService);
  private readonly loadingService: LoadingService = inject(LoadingService);
  private readonly callPermissionsService: CallPermissionsService = inject(CallPermissionsService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly webSocketController: WebSocketController = inject(WebSocketController);
  private readonly navigationStore: NavigationStore = inject(NavigationStore);
  private readonly navController: NavController = inject(NavController);
  private readonly router: Router = inject(Router);
  private readonly datePipe: DatePipe = inject(DatePipe);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly incomingCallOperations: Map<number, Promise<IncomingCallOpenResult>> = new Map<number, Promise<IncomingCallOpenResult>>();
  private readonly incomingCallAcceptOperations: Map<number, Promise<void>> = new Map<number, Promise<void>>();
  private readonly incomingSocketInitTimeoutMs: number = 30000;
  private readonly incomingAcceptResponseTimeoutMs: number = 30000;
  private readonly incomingAcceptRetryCount: number = 3;
  private readonly incomingAcceptRetryDelayMs: number = 2000;

  public async openIncomingCall(orderId: number): Promise<IncomingCallOpenResult> {
    const normalizedOrderId: number | null = this.normalizeOrderId(orderId);
    if (normalizedOrderId === null) {
      console.error('Order id is not defined!');
      await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
      return this.getIncomingCallFailedResult();
    }

    const existingOperation: Promise<IncomingCallOpenResult> | undefined = this.incomingCallOperations.get(normalizedOrderId);
    if (existingOperation) {
      return await existingOperation;
    }

    const operation: Promise<IncomingCallOpenResult> = this.openIncomingCallInternal(normalizedOrderId)
      .finally((): void => {
        this.incomingCallOperations.delete(normalizedOrderId);
      });

    this.incomingCallOperations.set(normalizedOrderId, operation);
    return await operation;
  }

  private async openIncomingCallInternal(orderId: number): Promise<IncomingCallOpenResult> {
    try {
      const response: AvatarOrderDataResponse = await this.callOrderService.getOrder(orderId, false);
      const order: AvatarOrderResponse = response?.data;
      if (!order) {
        console.error('Order data is not defined!');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }

      const isAudioCall: boolean = this.callOrderService.isAudioCallOrder(order);
      if (!isAudioCall) {
        console.error('Incoming call route received non-audio order type');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }

      return await this.prepareIncomingAudioCall(orderId, response);
    } catch (error) {
      console.error(error);
      await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
      return this.getIncomingCallFailedResult();
    }
  }

  private async prepareIncomingAudioCall(orderId: number, response?: AvatarOrderDataResponse): Promise<IncomingCallOpenResult> {
    try {
      const orderResponse: AvatarOrderDataResponse = response ?? await this.callOrderService.getOrder(orderId);

      const order: AvatarOrderResponse = orderResponse?.data;
      if (!order) {
        console.error('Order data is not defined!');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }
      if (!this.callOrderService.isValidOrder(order)) {
        if (this.callOrderService.isOrderFinal(order.status)) {
          console.warn('Order status: ', order.status);
        } else {
          console.error('Order is not valid!');
        }
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }
      const isAudioCall: boolean = this.callOrderService.isAudioCallOrder(order);
      if (!isAudioCall) {
        console.error('Order type is not audio call!');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }

      return {
        opened: true,
        navigationCommands: ['/audio-call'],
        orderId,
        shouldAcceptAudioInBackground: true,
        audioOrderResponse: orderResponse,
      };
    } catch (error) {
      console.error(error);
      await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
      return this.getIncomingCallFailedResult();
    }
  }

  public setupIncomingAudioCall(orderResponse: AvatarOrderDataResponse): void {
    this.callSessionService.setSelectedOrder(orderResponse, false);
    this.connectAudioService.subscribeToEvents();
  }

  public finalizeIncomingAudioCall(orderId: number): void {
    this.scheduleIncomingAudioAccept(orderId);
  }

  public rollbackIncomingAudioCall(orderId: number | null): void {
    if (orderId === null) return;
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (currentOrderId !== orderId) return;
    this.callSessionService.clearSelectedOrder();
  }

  public hasActiveAudioCall(): boolean {
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (currentOrderId === null) return false;
    return this.callSessionService.selectedOrder?.type === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
  }

  public isAudioCallScreenForOrder(url: string, orderId: number): boolean {
    const normalizedOrderId: number | null = this.normalizeOrderId(orderId);
    if (normalizedOrderId === null) return false;

    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (currentOrderId !== normalizedOrderId) return false;

    const currentPath: string = this.getPathFromUrl(url);
    return currentPath.includes('/audio-call');
  }

  public isIncomingCallScreenForOrder(url: string, orderId: number): boolean {
    const normalizedOrderId: number | null = this.normalizeOrderId(orderId);
    if (normalizedOrderId === null) return false;

    const currentPath: string = this.getPathFromUrl(url);
    return currentPath === `/incoming-call/${normalizedOrderId}`;
  }

  public async startOutgoingAudioCallFromUi(
    toUserId: number | null | undefined,
    options: { backUrl?: string | null; isGroupChat?: boolean } = {},
  ): Promise<boolean> {
    const backUrl: string | null = options.backUrl ?? null;
    const isGroupChat: boolean = options.isGroupChat ?? false;

    if (isGroupChat) {
      await this.toastService.error(this.translate.instant('chat.call.error'));
      return false;
    }

    const myUserId: number | undefined = this.authService.parsedToken?.userId;
    if (!toUserId || !myUserId || toUserId === myUserId) {
      await this.toastService.error(this.translate.instant('call.toasts.userNotDefined'));
      return false;
    }

    if (!this.webSocketController.socketConnected) {
      await this.toastService.error(this.translate.instant('call.toasts.socketNotConnected'));
      return false;
    }

    await this.waitForSocketInit();

    const hasMicrophoneAccess: boolean = await this.ensureMicrophonePermission();
    if (!hasMicrophoneAccess) return false;

    try {
      await this.loadingService.start();
      await this.startOutgoingAudioCall(toUserId, backUrl);
      return true;
    } catch (error) {
      if (error instanceof Error
        && (error.message === 'No order id in response' || error.message === 'No date from in request')) {
        console.error(error);
        await this.toastService.error(this.translate.instant('call.toasts.createOrderError'));
        return false;
      }

      const { status, message } = this.getCallErrorInfo(error);
      const isCallFailError: boolean = status === HttpStatusCode.Forbidden && message === CHAT_CALL_STATUS.FAIL;
      const messageKey: string = isCallFailError ? 'chat.call.fail' : 'chat.call.error';
      if (!isCallFailError) console.error(error);
      await this.toastService.error(this.translate.instant(messageKey));
      return false;
    } finally {
      await this.loadingService.stop();
    }
  }

  public async startOutgoingAudioCall(toUserId: number, backUrl: string | null): Promise<void> {
    const dateFromAt: string | null = this.datePipe.transform(Date.now(), 'yyyy-MM-dd HH:mm:ss');
    if (!dateFromAt) {
      throw new Error('No date from in request');
    }
    const response: AvatarOrderDataResponse = await this.callOrderService.createOutgoingAudioCallOrder(toUserId, dateFromAt);

    if (!response?.data?.id) {
      throw new Error('No order id in response');
    }

    this.callSessionService.setSelectedOrder(response, true);
    this.connectAudioService.subscribeToEvents();
    const resolvedBackUrl: string = backUrl || this.router.url;
    this.navigationStore.setBackUrl(resolvedBackUrl);
    await this.navController.navigateForward(['/audio-call'], {replaceUrl: true});
  }

  private async ensureMicrophonePermission(): Promise<boolean> {
    try {
      const permissionState: PermissionState = await this.callPermissionsService.getMicrophonePermission();
      if (permissionState === 'denied') {
        await this.toastService.warning(this.translate.instant('chat.call.microphonePermissionDenied'));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Microphone permission check failed', error);
      return true;
    }
  }

  private async waitForSocketInit(): Promise<void> {
    if (this.webSocketController.isSocketInitedSubject.value) return;
    if (!this.webSocketController.socketConnected) {
      this.webSocketController.socketConnect();
    }

    await firstValueFrom(this.webSocketController.isSocketInitedSubject.pipe(
      filter((inited: boolean): boolean => inited),
    ));
  }

  private scheduleIncomingAudioAccept(orderId: number): void {
    const existingOperation: Promise<void> | undefined = this.incomingCallAcceptOperations.get(orderId);
    if (existingOperation) {
      return;
    }

    const operation: Promise<void> = this.acceptIncomingAudioCall(orderId)
      .finally((): void => {
        this.incomingCallAcceptOperations.delete(orderId);
      });

    this.incomingCallAcceptOperations.set(orderId, operation);
  }

  private async acceptIncomingAudioCall(orderId: number): Promise<void> {
    let lastError: unknown = null;

    for (let attempt: number = 1; attempt <= this.incomingAcceptRetryCount; attempt++) {
      try {
        if (!this.webSocketController.socketConnected) {
          console.log('WebSocket not connected, waiting for connection...');
        }

        const socketReady: boolean = await this.waitForSocketInitWithTimeout(this.incomingSocketInitTimeoutMs);
        if (!socketReady) {
          throw new Error('Socket init timeout for incoming audio call');
        }

        await this.withTimeout(
          this.callOrderService.acceptIncomingAudioCall(orderId),
          this.incomingAcceptResponseTimeoutMs,
          'Incoming audio call accept timeout',
        );
        return;
      } catch (error) {
        lastError = error;
        console.warn('Incoming audio accept attempt failed', { orderId, attempt, error });
        if (attempt < this.incomingAcceptRetryCount) {
          await this.delay(this.incomingAcceptRetryDelayMs);
        }
      }
    }

    console.error('Incoming audio call accept failed after retries', { orderId, error: lastError });
    await this.handleIncomingAcceptFailure(orderId, lastError);
  }

  private async waitForSocketInitWithTimeout(timeoutMs: number): Promise<boolean> {
    if (this.webSocketController.isSocketInitedSubject.value) return true;
    if (!this.webSocketController.socketConnected) {
      this.webSocketController.socketConnect();
    }

    try {
      await firstValueFrom(this.webSocketController.isSocketInitedSubject.pipe(
        filter((inited: boolean): boolean => inited),
        timeout({first: timeoutMs}),
      ));
      return true;
    } catch (error) {
      console.warn('Socket init timeout for incoming audio call', { timeoutMs, error });
      return false;
    }
  }

  private async handleIncomingAcceptFailure(orderId: number, error: unknown): Promise<void> {
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (currentOrderId !== orderId) {
      return;
    }

    const isCallConnected: boolean = this.connectAudioService.webrtcAudioService.isConnected || this.connectAudioService.inStream;
    if (isCallConnected) {
      console.warn('Skipping incoming accept fallback because call is already connected', { orderId });
      return;
    }

    const currentUrl: string = (this.router.url ?? '').toLowerCase();
    const isIncomingCallFlow: boolean = currentUrl.includes('/audio-call') || currentUrl.includes('/incoming-call');
    if (!isIncomingCallFlow) {
      return;
    }

    const shouldKeepCallScreen: boolean = await this.shouldKeepCurrentCallAfterAcceptFailure(orderId, error);
    if (shouldKeepCallScreen) {
      return;
    }

    await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
    const shouldBypassCallDeactivateGuard: boolean = currentUrl.includes('/audio-call');
    if (shouldBypassCallDeactivateGuard) {
      this.connectAudioService.stopAccessSubject.next(true);
    }

    try {
      this.callSessionService.clearSelectedOrder();
      await this.router.navigate(['/calls'], {replaceUrl: true});
    } finally {
      if (shouldBypassCallDeactivateGuard) {
        this.connectAudioService.stopAccessSubject.next(false);
      }
    }
  }

  private async shouldKeepCurrentCallAfterAcceptFailure(orderId: number, error: unknown): Promise<boolean> {
    if (!this.isRecoverableIncomingAcceptError(error)) {
      return false;
    }

    const activeStatuses: Set<number> = new Set<number>([
      CALL_ORDER_STATUS_NUMBERS.ACCEPTED,
    ]);

    for (let attempt: number = 1; attempt <= 3; attempt++) {
      try {
        const orderResponse: AvatarOrderDataResponse = await this.callOrderService.getOrder(orderId, false);
        const order: AvatarOrderResponse = orderResponse?.data;
        const orderType: number | undefined = order?.type;
        const orderStatus: number | undefined = order?.status;

        if (orderType !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL) {
          return false;
        }

        if (typeof orderStatus === 'number' && activeStatuses.has(orderStatus)) {
          console.warn('Keeping call screen after recoverable accept failure because order is still active', {
            orderId,
            orderStatus,
            attempt,
          });
          return true;
        }

        if (this.callOrderService.isOrderFinal(orderStatus)) {
          return false;
        }
      } catch (statusError) {
        console.warn('Failed to verify order status after incoming accept failure', {
          orderId,
          attempt,
          statusError,
        });
      }

      if (attempt < 3) {
        await this.delay(this.incomingAcceptRetryDelayMs);
      }
    }

    console.warn('Keeping call screen after recoverable accept failure because order status could not be verified', {
      orderId,
    });
    return true;
  }

  private isRecoverableIncomingAcceptError(error: unknown): boolean {
    if (error instanceof Error) {
      const message: string = error.message.toLowerCase();
      return message.includes('timeout')
        || message.includes('network')
        || message.includes('socket')
        || message.includes('offline');
    }
    if (typeof error === 'string') {
      const message: string = error.toLowerCase();
      return message.includes('timeout')
        || message.includes('network')
        || message.includes('socket')
        || message.includes('offline');
    }
    return false;
  }

  private async delay(ms: number): Promise<void> {
    const timeoutMs: number = Math.max(0, Math.floor(ms));
    await new Promise<void>((resolve): void => {
      setTimeout(resolve, timeoutMs);
    });
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise: Promise<never> = new Promise<never>((_, reject): void => {
      timeoutId = setTimeout((): void => {
        reject(new Error(timeoutMessage));
      }, timeoutMs);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  private getIncomingCallFailedResult(): IncomingCallOpenResult {
    return {
      opened: false,
      navigationCommands: null,
      orderId: null,
      shouldAcceptAudioInBackground: false,
      audioOrderResponse: null,
    };
  }

  private getCallErrorInfo(error: unknown): { status: number | null; message: string | null } {
    if (typeof error !== 'object' || error === null) {
      return { status: null, message: null };
    }

    const statusValue: unknown = Reflect.get(error, 'status');
    const status: number | null = (typeof statusValue === 'number') ? statusValue : null;

    const resultValue: unknown = Reflect.get(error, 'result');
    if (typeof resultValue !== 'object' || resultValue === null) {
      return { status, message: null };
    }

    const messageValue: unknown = Reflect.get(resultValue, 'message');
    const message: string | null = (typeof messageValue === 'string') ? messageValue : null;
    return { status, message };
  }

  private normalizeOrderId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value > 0) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed: string = value.trim();
      if (!trimmed.length) return null;
      const parsed: number = Number(trimmed);
      if (Number.isFinite(parsed) && Number.isInteger(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return null;
  }

  private getPathFromUrl(url: string): string {
    const rawUrl: string = typeof url === 'string' ? url : '';
    const urlWithoutQuery: string = rawUrl.split('?')[0];
    const pathWithoutFragment: string = urlWithoutQuery.split('#')[0];
    return pathWithoutFragment.toLowerCase();
  }
}
